import { useEffect, useState } from 'react';
import { toast } from '@/stores/toast-store';
import { Modal } from '@/components/ui/modal';
import {
  useRecordPayment,
  type CreditAccountSummary,
  type RecordPaymentInput,
} from '@/hooks/customer-engagement/use-credit-accounts';
import { formatCurrency } from '@/lib/utils';
import { useLocationStore } from '@/stores/location-store';

interface Props {
  open: boolean;
  customer: CreditAccountSummary | null;
  onClose: () => void;
}

const inputBase = 'h-9 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:ring-1';
const inputNormal = `${inputBase} border-input focus:border-primary focus:ring-primary`;
const inputError = `${inputBase} border-red-500 focus:border-red-500 focus:ring-red-500`;
const textareaBase = 'w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-1';
const textareaNormal = `${textareaBase} border-input focus:border-primary focus:ring-primary`;

const EMPTY: RecordPaymentInput = {
  amount: 0,
  method: 'cash',
};

export function RecordPaymentDialog({ open, customer, onClose }: Props) {
  const [form, setForm] = useState<RecordPaymentInput>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const record = useRecordPayment();
  // The credit payment must carry the location it was recorded for, so it lands
  // in that location's "A/R - Collected" report row (the sales-summary A/R query
  // filters credit_account_payments by location_id). Without it, a
  // dashboard-recorded payment has a NULL location and is dropped from any
  // location-scoped report — the reason it wasn't showing up.
  const { selectedLocationId } = useLocationStore();

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setFieldErrors({});
  }, [open]);

  const handleClose = () => {
    setForm(EMPTY);
    setFieldErrors({});
    onClose();
  };

  const set = <K extends keyof RecordPaymentInput>(key: K, value: RecordPaymentInput[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (fieldErrors[key as string]) {
      setFieldErrors((e) => {
        const { [key as string]: _, ...rest } = e;
        return rest;
      });
    }
  };

  if (!customer) return null;

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!Number.isFinite(form.amount) || form.amount <= 0) {
      errors.amount = 'Amount must be greater than 0';
    }
    if ((form.referenceNumber?.length ?? 0) > 120) {
      errors.referenceNumber = 'Reference number must be 120 characters or fewer';
    }
    if ((form.notes?.length ?? 0) > 500) {
      errors.notes = 'Notes must be 500 characters or fewer';
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const res = await record.mutateAsync({
        customerId: customer.customerId,
        input: {
          ...form,
          referenceNumber: form.referenceNumber?.trim() || undefined,
          notes: form.notes?.trim() || undefined,
          locationId: selectedLocationId || undefined,
        },
      });
      toast.success(`Payment recorded — new balance ${formatCurrency(Number(res.data.newBalance))}`);
      handleClose();
    } catch (e: any) {
      toast.error(e?.error?.message ?? 'Failed to record payment');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`Record Payment — ${customer.customerName}`}
      widthClass="max-w-[560px]"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="record-payment-form"
            disabled={record.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {record.isPending ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      }
    >
      <div className="mb-4 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
        Current balance: <strong className="text-foreground">{formatCurrency(Number(customer.currentBalance))}</strong>
      </div>

      <form id="record-payment-form" onSubmit={handleSubmit} noValidate className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">
              Amount ($) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={form.amount || ''}
              onChange={(e) => set('amount', Number(e.target.value))}
              autoFocus
              className={`mt-1 ${fieldErrors.amount ? inputError : inputNormal}`}
              aria-invalid={!!fieldErrors.amount}
            />
            {fieldErrors.amount && <p className="mt-1 text-xs text-red-500">{fieldErrors.amount}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium">
              Method <span className="text-red-500">*</span>
            </label>
            <select
              value={form.method}
              onChange={(e) => set('method', e.target.value as RecordPaymentInput['method'])}
              className={`mt-1 ${inputNormal}`}
            >
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="check">Check</option>
              <option value="ach">ACH</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium">Reference number (check #, transaction ID, etc.)</label>
          <input
            type="text"
            value={form.referenceNumber ?? ''}
            onChange={(e) => set('referenceNumber', e.target.value)}
            maxLength={120}
            className={`mt-1 ${fieldErrors.referenceNumber ? inputError : inputNormal}`}
            aria-invalid={!!fieldErrors.referenceNumber}
          />
          {fieldErrors.referenceNumber && <p className="mt-1 text-xs text-red-500">{fieldErrors.referenceNumber}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium">Notes</label>
          <textarea
            value={form.notes ?? ''}
            onChange={(e) => set('notes', e.target.value)}
            rows={2}
            maxLength={500}
            className={`mt-1 ${textareaNormal}`}
          />
          {fieldErrors.notes && <p className="mt-1 text-xs text-red-500">{fieldErrors.notes}</p>}
        </div>
      </form>
    </Modal>
  );
}
